import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { hasVendorDeviceSelection, useOptionMetaVendorDeviceSelection } from '@/delivery-type-plugin/components';
import type { VendorDeviceSelection } from '@/delivery-type-plugin/components';
import { isFieldInteractionLocked } from '@/delivery-type-plugin/platform/capability-registry/fieldControl';
import { isFieldVisible } from '@/delivery-type-plugin/platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '@/delivery-type-plugin/platform/capability-registry/shouldSkipCapabilityFetch';

import { BillingResourceSelector } from './components/BillingResourceSelector';
import { PhysicalResourceSelector } from './components/PhysicalResourceSelector';
import { EBM_RESOURCE_CONFIG_FIELD_KEYS, EBM_RESOURCE_CONFIG_OPTION_META_KEYS } from './fieldKeys';
import type { EbmResourceConfigBlockProps, EbmResourceConfigPaths } from './types';
import { clearBillingResourceSelection } from './utils/billingResourceMeta';

const FormItem = Form.Item;

/** 无预置空态清理：同一 form 实例只执行一次，避免 remount 冲掉用户已选导致预览要选两次 */
const clearedEmptySelectionForms = new WeakSet<object>();

/** SKU 预置是否已带节点/厂商设备（用于下单空态判断） */
function hasPresetEbmNode(preset?: Record<string, unknown>): boolean {
  if (!preset) return false;
  const resourceConfiguration = preset.resourceConfiguration;
  if (typeof resourceConfiguration === 'string' && resourceConfiguration.trim()) return true;
  const optionMeta = preset.optionMeta as Record<string, unknown> | undefined;
  const nodeMeta = optionMeta?.[EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode] as VendorDeviceSelection | undefined;
  return hasVendorDeviceSelection(nodeMeta);
}

/** 弹性裸金属资源配置：计价模式 VendorAndDeviceRadioList，非计价模式物理配置卡片 */
export function EbmResourceConfigBlock(props: EbmResourceConfigBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as EbmResourceConfigPaths;
  const { t } = useTranslation();

  const billModelId = options?.billModelId;
  const deploymentTypeId = options?.deploymentTypeId;
  /** 计价模式：展示厂商设备；物理选择 UI hidden，字段仍保留供提交 */
  const billingActive = Boolean(billModelId);

  const readonly = isFieldInteractionLocked(EBM_RESOURCE_CONFIG_FIELD_KEYS.configuration, control) || options?.isEdit === true;

  /**
   * 厂商设备列表：preview / 显式 skipFetch / 固定规格下单不请求，仅展示预置选型。
   */
  const skipVendorDeviceFetch =
    options?.resourceEditMode === 'order-fixed' ||
    shouldSkipCapabilityFetch(control, {
      skipFetch: options?.skipFetch,
    });
  /**
   * 物理配置列表：计价模式不拉（后端保障完整性）；preview / 固定规格跳过。
   * 只读下单仍拉取以便展示已选 composition（卡片可点选由 readOnly 禁止）。
   */
  const skipPhysicalListFetch = billingActive || skipVendorDeviceFetch;

  const [vendorDeviceSelection, setVendorDeviceSelection] = useOptionMetaVendorDeviceSelection(
    form,
    EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode,
  );

  const presetHasNode = useMemo(() => hasPresetEbmNode(options?.presetConfigSnapshot), [options?.presetConfigSnapshot]);
  const resourceEditMode = options?.resourceEditMode;

  /**
   * 自定义/后付费下单：SKU 无节点预置时清空残留选中（勿默认第一条）。
   * 每个 form 只清一次；预置从无→有时重置标记，便于再次进入空态 SKU 时仍可清理。
   */
  useEffect(() => {
    if (resourceEditMode !== 'order-custom' && resourceEditMode !== 'order-post-paid') return;
    if (presetHasNode) {
      clearedEmptySelectionForms.delete(form);
      return;
    }
    if (clearedEmptySelectionForms.has(form)) return;
    clearedEmptySelectionForms.add(form);
    clearBillingResourceSelection(form, paths);
    setVendorDeviceSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随预置/模式切换清空残留
  }, [presetHasNode, resourceEditMode]);

  if (!isFieldVisible(EBM_RESOURCE_CONFIG_FIELD_KEYS.configuration, control)) {
    return null;
  }

  return (
    <>
      {billingActive && billModelId ? (
        <BillingResourceSelector
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          form={form}
          paths={paths}
          disabled={readonly}
          skipFetch={skipVendorDeviceFetch}
          value={vendorDeviceSelection}
          onSelectionChange={setVendorDeviceSelection}
          options={options}
        />
      ) : null}

      {/*
        非计费节点选择：计价时 FormItem hidden + Input 承接 value；
        计价选中写入计费 composition 映射（含 vendorID/deviceID），后端再补齐完整性。
      */}
      <FormItem
        field={paths.resourceConfiguration}
        hidden={billingActive}
        style={{ margin: 0 }}
        rules={
          readonly || billingActive ? undefined : [{ required: true, message: t('data-center:please.select.a.resource.configuration') }]
        }
      >
        {billingActive ? (
          <Input />
        ) : (
          <PhysicalResourceSelector form={form} paths={paths} readOnly={readonly} skipListFetch={skipPhysicalListFetch} />
        )}
      </FormItem>
    </>
  );
}
